import axios, { AxiosError } from 'axios'

export type Role = 'PV_USER' | 'PV_ADMIN'
export type UserProfile = {
  id?: number
  userId?: number
  fullName?: string
  profileImageUrl?: string
  locale?: string
}
export type TokenData = {
  accessToken?: string
  refreshToken?: string
  tokenType?: string
  expiresIn?: number
  userProfile?: UserProfile
  isFirstTimeUser?: boolean
}
export type ApiResponse<T> = {
  success: boolean
  message?: string
  data?: T
  error?: { code?: string; details?: string; field?: string }
  timestamp?: string
}

export type LocationInfo = {
  latitude?: string | null
  longitude?: string | null
  ip_address?: string | null
}

export type DeviceInfo = {
  device_id?: string | null
  device_type?: string | null
  os_version?: string | null
  user_agent?: string | null
}

export type MessageItem = {
  id?: number | null
  userId?: number
  source?: string
  sender?: string
  rawContent?: string
  messageDate?: string
  timestamp?: string
  receivedTimestamp?: string | null
  locationInfo?: LocationInfo | null
  deviceInfo?: DeviceInfo | null
  transactionId?: number | null
  createdAt?: string | null
  updatedAt?: string | null
  transactionType?: string
  amount?: number
  merchantName?: string | null
  accountNumber?: string | null
  bankName?: string | null
  referenceNumber?: string | null
  categoryName?: string | null
  subCategoryName?: string | null
  confidenceLevel?: number | null
}
export type Page<T> = {
  content: T[]
  pageNumber: number
  pageSize: number
  totalElements: number
  totalPages: number
  last: boolean
  first: boolean
}

export type CreditCardDue = {
  cardMask?: string
  dueDate?: string
  dueAmount?: number
  minimumDue?: number
  currency?: string
}

export type CategorizeResponse = {
  merchant?: string
  category?: string
  subcategory?: string
  billOrDue?: boolean
  transactionDirection?: string
  transactionAmount?: number
  transactionCurrency?: string
  bankAccount?: string
  referenceNumber?: string
  balance?: number
  balanceCurrency?: string
  creditCardDue?: CreditCardDue
  upi?: string
}

// Admin API Types
export type AdminUser = {
  id?: number | null
  userId?: number | null
  fullName?: string | null
  mobileNumber?: string | null
  email?: string | null
  dateOfBirth?: string | null
  gender?: string | null
  profileImageUrl?: string | null
  addressLine1?: string | null
  addressLine2?: string | null
  city?: string | null
  state?: string | null
  pincode?: string | null
  country?: string | null
  pan?: string | null
  aadhaar?: string | null
  smsPermission?: boolean | null
  emailPermission?: boolean | null
  notificationPermission?: boolean | null
  pushNotificationPermission?: boolean | null
  locationPermission?: boolean | null
  dataSharingPermission?: boolean | null
  locale?: string | null
  deviceMetadata?: {
    deviceId?: string | null
    deviceType?: string | null
    osVersion?: string | null
  } | null
  locationInfo?: LocationInfo | null
  joinedDate?: string | null
  stats?: UserHealthStats | null
  attribution?: Attribution | null
  permissions?: {
    smsPermission?: boolean
    notificationPermission?: boolean
    locationPermission?: boolean
  }
  createdAt?: string | null
}

export type AcquisitionSource = 'google_ads' | 'meta_ads' | 'organic' | 'unknown'

// Install-attribution (UTM / acquisition source). Null when the user has no
// attribution row (pre-attribution installs, non-Android, or never posted).
export type Attribution = {
  acquisitionSource: AcquisitionSource | string
  utmSource?: string | null
  utmMedium?: string | null
  utmCampaign?: string | null
  utmContent?: string | null
  gclid?: string | null
  installReferrer?: string | null
  platform?: string | null
  appVersion?: string | null
  attributedAt?: string | null
}

export type UserHealthStats = {
  smsParsedCount: number
  manualEntryCount: number
  chatMessageCount: number
  deletedTransactionCount: number
  ignoredTransactionCount: number
  duplicateTransactionCount: number
  tokenUsage?: number
}

export type AdminTransaction = {
  id?: number | null
  userId?: number | null
  transactionDate?: string | null
  transactionType?: string | null
  category?: string | null
  amount?: number | null
  currency?: string | null
  description?: string | null
  merchant?: string | null
  subcategory?: string | null
  accountNumber?: string | null
  bankName?: string | null
  referenceNumber?: string | null
  source?: string | null
  isRecurring?: boolean | null
  isVerified?: boolean | null
  notes?: string | null
  tags?: string | null
  location?: string | null
  balance?: number | null
  createdAt?: string | null
  isIgnored?: boolean | null
  isDuplicate?: boolean | null
  duplicateOfTransactionId?: string | null
  rawSms?: string | null

}

export type DeletedTransactionDoc = {
  id?: string | null
  originalTransactionId?: string | null
  userId?: number | null
  amount?: number | null
  merchant?: string | null
  deletedAt?: string | null
  deletedReason?: string | null
  deletedBy?: string | null
}

export type CategoryInsight = {
  userId?: number | null
  category?: string | null
  totalTransactions?: number | null
  debitCount?: number | null
  creditCount?: number | null
  totalDebit?: number | null
  totalCredit?: number | null
}

export type MerchantInsight = {
  userId?: number | null
  merchantName?: string | null
  transactionCount?: number | null
  totalAmount?: number | null
  debitCount?: number | null
  creditCount?: number | null
}

export type AnalyticsOverview = {
  totalUsers?: number | null
  totalTransactions?: number | null
  totalMessages?: number | null
  totalDebit?: number | null
  totalCredit?: number | null
  netAmount?: number | null
  categorySpending?: Record<string, number> | null
  topMerchants?: Record<string, number> | null
}

const ACCESS_KEY = 'auth_token'
const REFRESH_KEY = 'refresh_token'
const USER_KEY = 'auth_user'

function getAccessToken() {
  try { return localStorage.getItem(ACCESS_KEY) || undefined } catch { return undefined }
}
function getRefreshToken() {
  try { return localStorage.getItem(REFRESH_KEY) || undefined } catch { return undefined }
}
function setSession(access?: string, refresh?: string, user?: any) {
  try {
    if (access) localStorage.setItem(ACCESS_KEY, access)
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh)
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user))
  } catch { }
}
function clearSession() {
  try {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
  } catch { }
}

export const getSelectedEnvironment = (): 'prod' | 'dev' => {
  try {
    const saved = localStorage.getItem('backend_env')
    if (saved === 'dev') return 'dev'
    if (saved === 'prod') return 'prod'
  } catch {}
  return 'prod'
}

export const setSelectedEnvironment = (env: 'prod' | 'dev') => {
  try {
    localStorage.setItem('backend_env', env)
    // Clear session when changing environments to avoid auth issues
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
    localStorage.removeItem(USER_KEY)
  } catch {}
}

const getBaseURL = () => {
  const selected = getSelectedEnvironment()
  if (selected === 'dev') {
    return 'https://dev-api.habitmoney.in'
  }
  return 'https://server.habitmoney.in'
}

export const client = axios.create({
  baseURL: getBaseURL(),
  withCredentials: true,
  maxContentLength: 100 * 1024 * 1024, // 100MB
  maxBodyLength: 100 * 1024 * 1024 // 100MB
})

client.interceptors.request.use(config => {
  // Don't add Authorization header for refresh token endpoint
  if (config.url?.includes('/api/auth/refresh')) {
    return config
  }
  const token = getAccessToken()
  if (token) {
    config.headers = { ...(config.headers || {}), Authorization: `Bearer ${token}` } as any
  }
  return config
})

let isRefreshing = false
let pending: Array<(t?: string) => void> = []

client.interceptors.response.use(
  r => r,
  async (error: AxiosError) => {
    const status = error.response?.status
    const original = error.config
    if (status === 401 && original && !original.url?.includes('/api/auth/refresh')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pending.push((token) => {
            if (token) {
              if (original.headers) {
                original.headers.Authorization = `Bearer ${token}`
              }
              resolve(client(original))
            } else {
              reject(error)
            }
          })
        })
      }
      isRefreshing = true
      try {
        const refreshed = await refreshAccessToken()
        if (refreshed && original.headers) {
          original.headers.Authorization = `Bearer ${refreshed}`
        }
        pending.forEach(fn => fn(refreshed))
        pending = []
        const result = await client(original)
        isRefreshing = false
        return result
      } catch (e) {
        pending.forEach(fn => fn(undefined))
        pending = []
        isRefreshing = false
        clearSession()
        // Redirect to login if we have window object
        if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        throw e
      }
    }
    throw error
  }
)

export async function refreshAccessToken(): Promise<string | undefined> {
  const rt = getRefreshToken()
  if (!rt) return undefined
  const { data } = await client.post('/api/auth/refresh', { refreshToken: rt }, { headers: { accept: '*/*' } })
  const payload = (data as ApiResponse<TokenData>).data
  const at = payload?.accessToken
  const newRt = payload?.refreshToken || rt
  setSession(at, newRt)
  return at
}

export async function loginWithEmailPassword({
  email,
  password,
  remember,
  role = 'PV_USER'
}: { email: string; password: string; remember: boolean; role?: Role }): Promise<TokenData> {
  const payload = { email, password, role }
  const { data } = await client.post('/api/auth/login/email-password', payload, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  const resp = data as ApiResponse<TokenData>
  const td = resp.data || {}
  const at = td.accessToken
  const rt = td.refreshToken
  const user = td.userProfile
  if (remember) setSession(at, rt, user)
  return td
}

// Google Login - uses app backend (same base URL as other APIs, e.g. http://localhost:8080)
export async function loginWithGoogle(
  idToken: string,
  options?: { fcm_token?: string; device_id?: string }
): Promise<TokenData> {
  const payload: { idToken: string; fcm_token?: string; device_id?: string } = { idToken }
  if (options?.fcm_token) payload.fcm_token = options.fcm_token
  if (options?.device_id) payload.device_id = options.device_id

  const { data } = await client.post('/api/auth/login/google', payload, {
    headers: { 'Content-Type': 'application/json', accept: '*/*' }
  })
  const raw = (data as ApiResponse<TokenData>)?.data ?? (data as Record<string, unknown>)
  const at =
    (raw as TokenData).accessToken ??
    (raw as { access_token?: string }).access_token
  const rt =
    (raw as TokenData).refreshToken ??
    (raw as { refresh_token?: string }).refresh_token
  const userProfile =
    (raw as TokenData).userProfile ??
    ((raw as { user_id?: number }).user_id != null
      ? { userId: (raw as { user_id: number }).user_id }
      : undefined)
  if (at) setSession(at, rt, userProfile)
  return {
    accessToken: at,
    refreshToken: rt,
    userProfile,
  } as TokenData
}


export async function getUserMessages({ userId, page = 0, size = 20 }: { userId: number; page?: number; size?: number }): Promise<Page<MessageItem>> {
  const { data } = await client.get(`/api/analytics/users/${userId}/messages`, {
    params: { page, size },
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<MessageItem>>
  return (resp.data as Page<MessageItem>)
}

export async function registerEmailPassword({
  email,
  mobile,
  password,
  role = 'PV_USER'
}: { email: string; mobile: string; password: string; role?: Role }): Promise<any> {
  const payload = { email, mobile, password, role }
  const { data } = await client.post('/api/auth/register/email-password', payload, {
    headers: { accept: '*/*' }
  })
  return data
}

export async function verifyEmail({ token }: { token: string }): Promise<{ status?: string }> {
  const { data } = await client.post('/api/auth/verify-email', { token }, {
    headers: { accept: '*/*' }
  })
  return data
}

export async function categorizeSms({ header, smsText }: { header: string; smsText: string }): Promise<CategorizeResponse> {
  const { data } = await client.post('/api/v1/categorize', { header, smsText }, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as CategorizeResponse
}

// Admin API Functions
export async function getAdminUsers({
  page = 0,
  size = 20,
  search,
  permissionStatus,
  source,
  sortBy,
  sortDirection
}: {
  page?: number
  size?: number
  search?: string
  permissionStatus?: string
  source?: AcquisitionSource | string
  sortBy?: string
  sortDirection?: 'ASC' | 'DESC'
}): Promise<Page<AdminUser>> {
  const params: any = { page, size }
  if (search) params.search = search
  if (permissionStatus) params.permissionStatus = permissionStatus
  if (source) params.source = source
  if (sortBy) params.sortBy = sortBy
  if (sortDirection) params.sortDirection = sortDirection

  const { data } = await client.get('/api/admin/users', {
    params,
    headers: { accept: '*/*' } as any
  })
  const resp = data as ApiResponse<Page<AdminUser>>
  return resp.data as Page<AdminUser>
}

export async function deleteUser(userId: number): Promise<ApiResponse<void>> {
  const { data } = await client.delete(`/api/profile/users/${userId}`, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<void>
}

export async function getAdminTransactions({
  page = 0,
  size = 20,
  userId,
  mobileNumber,
  startDate,
  endDate,
  category,
  transactionType,
  source,
  isIgnored,
  isDuplicate
}: {
  page?: number
  size?: number
  userId?: number
  mobileNumber?: string
  startDate?: string
  endDate?: string
  category?: string
  transactionType?: string
  source?: string
  isIgnored?: boolean
  isDuplicate?: boolean
}): Promise<Page<AdminTransaction>> {
  const params: Record<string, any> = { page, size }
  if (userId !== undefined) params.userId = userId
  if (mobileNumber) params.mobileNumber = mobileNumber
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  if (category) params.category = category
  if (transactionType) params.transactionType = transactionType
  if (source) params.source = source
  if (isIgnored !== undefined) params.isIgnored = isIgnored
  if (isDuplicate !== undefined) params.isDuplicate = isDuplicate

  const { data } = await client.get('/api/admin/transactions', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<AdminTransaction>>
  return resp.data as Page<AdminTransaction>
}

export async function getDeletedTransactions({
  page = 0,
  size = 20,
  userId,
  mobileNumber,
  startDate,
  endDate
}: {
  page?: number
  size?: number
  userId?: number
  mobileNumber?: string
  startDate?: string
  endDate?: string
}): Promise<Page<DeletedTransactionDoc>> {
  const params: Record<string, any> = { page, size }
  if (userId !== undefined) params.userId = userId
  if (mobileNumber) params.mobileNumber = mobileNumber
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate

  const { data } = await client.get('/api/admin/deleted-transactions', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<DeletedTransactionDoc>>
  return resp.data as Page<DeletedTransactionDoc>
}

export async function getDuplicateTransactions({
  page = 0,
  size = 20,
  userId,
  mobileNumber,
  startDate,
  endDate
}: {
  page?: number
  size?: number
  userId?: number
  mobileNumber?: string
  startDate?: string
  endDate?: string
}): Promise<Page<AdminTransaction>> {
  const params: Record<string, any> = { page, size }
  if (userId !== undefined) params.userId = userId
  if (mobileNumber) params.mobileNumber = mobileNumber
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate

  const { data } = await client.get('/api/admin/duplicate-transactions', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<AdminTransaction>>
  return resp.data as Page<AdminTransaction>
}

export type DeduplicateResponse = {
  userId?: number
  totalTransactions?: number
  duplicateGroupsFound?: number
  transactionsDeleted?: number
  deletedTransactionIds?: string[]
}

export async function deduplicateTransactions(userId: number): Promise<ApiResponse<DeduplicateResponse>> {
  const { data } = await client.post(`/api/transactions/deduplicate/users/${userId}`, {}, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<DeduplicateResponse>
}

export async function getAdminMessages({
  page = 0,
  size = 20,
  userId,
  source,
  startDate,
  endDate
}: {
  page?: number
  size?: number
  userId?: number
  source?: 'SMS' | 'EMAIL'
  startDate?: string
  endDate?: string
}): Promise<Page<MessageItem>> {
  const params: Record<string, any> = { page, size }
  if (userId !== undefined) params.userId = userId
  if (source) params.source = source
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  const { data } = await client.get('/api/admin/messages', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<MessageItem>>
  return resp.data as Page<MessageItem>
}

export async function getUserCategoryInsights({
  userId,
  startDate,
  endDate
}: {
  userId?: number
  startDate?: string
  endDate?: string
}): Promise<CategoryInsight[]> {
  const params: Record<string, any> = {}
  if (userId !== undefined) params.userId = userId
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  const { data } = await client.get('/api/admin/analytics/user-categories', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<CategoryInsight[]>
  return resp.data || []
}

export async function getUserMerchantInsights({
  userId,
  startDate,
  endDate
}: {
  userId?: number
  startDate?: string
  endDate?: string
}): Promise<MerchantInsight[]> {
  const params: Record<string, any> = {}
  if (userId !== undefined) params.userId = userId
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  const { data } = await client.get('/api/admin/analytics/user-merchants', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<MerchantInsight[]>
  return resp.data || []
}

// --- Admin Chat API ---

export type AdminChatConversationDTO = {
  conversationId: string
  userId: number
  userFullName?: string
  userMobileNumber?: string
  title?: string
  createdAt?: string
  updatedAt?: string
  messageCount: number
  // Session Metrics
  firstMessage?: string
  lastMessage?: string
  lastMessageAt?: string
  totalTokens?: number
  avgLatency?: number
  hasPositiveFeedback?: boolean
  status?: string // "Active" | "Ended"
}

export type AdminChatMessageDTO = {
  messageId?: string
  conversationId?: string
  userId?: number
  userFullName?: string
  userMessage?: string
  aiResponse?: string
  timestamp?: string
  promptTokens?: number
  completionTokens?: number
  totalTokens?: number
  userFeedback?: 'THUMBS_UP' | 'THUMBS_DOWN' | null
  latencyMs?: number
}

export type SubscriptionDetailsResponse = {
  subscriptionPlan?: string
  tokenLimit?: number
  tokensUsed?: number
  messagesSent?: number
  limitReached?: boolean
  nextResetDate?: string
}

// ... (previous imports and types)

// Existing function - remove /v1
export async function getAdminChatConversations({
  page = 0,
  size = 20,
  search,
  userId,
  mobileNumber,
  startDate,
  endDate
}: {
  page?: number
  size?: number
  search?: string
  userId?: number
  mobileNumber?: string
  startDate?: string
  endDate?: string
}): Promise<Page<AdminChatConversationDTO>> {
  const params: Record<string, any> = { page, size }
  if (search) params.search = search
  if (userId !== undefined) params.userId = userId
  if (mobileNumber) params.mobileNumber = mobileNumber
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate

  const { data } = await client.get('/api/admin/chat/conversations', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<AdminChatConversationDTO>>
  return resp.data as Page<AdminChatConversationDTO>
}

// Fetch individual chat messages. 
// If conversationId is provided, use the path-based endpoint for targeted retrieval.
export async function getAdminChatMessages({
  page = 0,
  size = 20,
  conversationId,
  userId,
  search,
  mobileNumber
}: {
  page?: number
  size?: number
  conversationId?: string
  userId?: number
  search?: string
  mobileNumber?: string
}): Promise<Page<AdminChatMessageDTO>> {
  // If we have a specific conversationId, the most reliable endpoint is the path-based one
  if (conversationId) {
    const { data } = await client.get(`/api/v1/chat/conversations/${encodeURIComponent(conversationId)}/messages`, {
      params: { page, size },
      headers: { accept: '*/*' }
    })
    // Wrap the response data as it might be raw or inside ApiResponse
    const resp = data as (ApiResponse<Page<AdminChatMessageDTO>> | Page<AdminChatMessageDTO>)
    return ('data' in resp) ? resp.data as Page<AdminChatMessageDTO> : resp as Page<AdminChatMessageDTO>
  }

  const params: Record<string, any> = { page, size }
  if (userId !== undefined) params.userId = userId
  if (search) params.search = search
  if (mobileNumber) params.mobileNumber = mobileNumber

  const { data } = await client.get('/api/admin/chat/messages', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<AdminChatMessageDTO>>
  return resp.data as Page<AdminChatMessageDTO>
}

// Existing function - remove /v1
export async function getUserChatUsage(userId: number): Promise<SubscriptionDetailsResponse> {
  const { data } = await client.get(`/api/admin/chat/users/${userId}/usage`, {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<SubscriptionDetailsResponse>
  return resp.data as SubscriptionDetailsResponse
}



export async function getAnalyticsOverview({
  startDate,
  endDate
}: {
  startDate?: string
  endDate?: string
}): Promise<AnalyticsOverview> {
  const params: Record<string, any> = {}
  if (startDate) params.startDate = startDate
  if (endDate) params.endDate = endDate
  const { data } = await client.get('/api/admin/analytics/overview', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AnalyticsOverview>
  return resp.data || {}
}

// Acquisition-source split (install attribution). Percentages are of
// totalAttributed, so they sum to ~100; noAttributionRow sits outside that.
export type AcquisitionMetrics = {
  totalUsers: number
  totalAttributed: number
  noAttributionRow: number
  bySource: Record<string, number>
  bySourcePercent: Record<string, number>
}

export async function getAcquisitionMetrics(): Promise<AcquisitionMetrics> {
  const { data } = await client.get('/api/admin/analytics/acquisition', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AcquisitionMetrics>
  return resp.data as AcquisitionMetrics
}

// Config Management Types
export type ConfigItem = {
  id?: string
  key?: string
  value?: string
  description?: string
  createdAt?: string
  updatedAt?: string
}

// Config Management API Functions
export async function getConfigs(): Promise<ConfigItem[]> {
  const { data } = await client.get('/api/config', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<ConfigItem[]>
  return resp.data || []
}

export async function getConfigByKey(key: string): Promise<ConfigItem> {
  const { data } = await client.get(`/api/config/${encodeURIComponent(key)}`, {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<ConfigItem>
  return resp.data as ConfigItem
}

export async function createConfig(key: string, value: string, description?: string): Promise<ApiResponse<ConfigItem>> {
  const payload: { key: string; value: string; description?: string } = { key, value }
  if (description) payload.description = description
  const { data } = await client.post('/api/config', payload, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<ConfigItem>
}

export async function updateConfig(key: string, value: string, description?: string): Promise<ApiResponse<ConfigItem>> {
  const payload: { value: string; description?: string } = { value }
  if (description !== undefined) payload.description = description
  const { data } = await client.put(`/api/config/${encodeURIComponent(key)}`, payload, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<ConfigItem>
}

export async function deleteConfig(key: string): Promise<ApiResponse<void>> {
  const { data } = await client.delete(`/api/config/${encodeURIComponent(key)}`, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<void>
}

// Subscription Management Types
export type SubscriptionConfig = {
  id?: string
  configId?: string
  name?: string
  price?: number
  currency?: string
  aiChatLimit?: number
  aiInsightsLimit?: number
  tokenLimit?: number
  description?: string
  isActive?: boolean
  paypalProductId?: string | null
  paypalPlanId?: string | null
}

export type CreateSubscriptionConfigRequest = {
  configId: string
  name: string
  price: number
  currency?: string
  aiChatLimit: number
  aiInsightsLimit: number
  tokenLimit: number
  description?: string
  isActive?: boolean
}

export type UpdateSubscriptionConfigRequest = {
  name?: string
  price?: number
  aiChatLimit?: number
  aiInsightsLimit?: number
  tokenLimit?: number
  description?: string
  isActive?: boolean
}

export type Subscription = {
  subscriptionId?: string
  userId?: number
  planId?: string
  status?: string
  startDate?: string
  endDate?: string
  nextBillingDate?: string
  paypalSubscriptionId?: string | null
  isTrial?: boolean
  trialEndDate?: string | null
  isCancelled?: boolean
  cancelledAt?: string | null
}

export type RevenueSummary = {
  monthlyRevenue?: number
  activeSubscriptions?: number
  churnRate?: number
  freeUsers?: number
  basicUsers?: number
  proUsers?: number
}

// Subscription Config Management API Functions
export async function getSubscriptionConfigs(activeOnly?: boolean): Promise<SubscriptionConfig[]> {
  const params: Record<string, any> = {}
  if (activeOnly) params.activeOnly = true
  const { data } = await client.get('/api/v1/admin/subscription-configs', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<SubscriptionConfig[]>
  return resp.data || []
}

export async function getSubscriptionConfigById(configId: string): Promise<SubscriptionConfig> {
  const { data } = await client.get(`/api/v1/admin/subscription-configs/${encodeURIComponent(configId)}`, {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<SubscriptionConfig>
  return resp.data as SubscriptionConfig
}

export async function createSubscriptionConfig(request: CreateSubscriptionConfigRequest): Promise<ApiResponse<SubscriptionConfig>> {
  const { data } = await client.post('/api/v1/admin/subscription-configs', request, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<SubscriptionConfig>
}

export async function updateSubscriptionConfig(configId: string, request: UpdateSubscriptionConfigRequest): Promise<ApiResponse<SubscriptionConfig>> {
  const { data } = await client.put(`/api/v1/admin/subscription-configs/${encodeURIComponent(configId)}`, request, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<SubscriptionConfig>
}

export async function deleteSubscriptionConfig(configId: string): Promise<ApiResponse<void>> {
  const { data } = await client.delete(`/api/v1/admin/subscription-configs/${encodeURIComponent(configId)}`, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<void>
}

// Admin Billing API Functions
export async function getAdminSubscriptions({
  page = 0,
  size = 20,
  plan,
  status
}: {
  page?: number
  size?: number
  plan?: string
  status?: string
}): Promise<Subscription[]> {
  const params: Record<string, any> = { page, size }
  if (plan) params.plan = plan
  if (status) params.status = status
  const { data } = await client.get('/api/v1/admin/billing/subscriptions', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Subscription[]>
  return resp.data || []
}

export async function getRevenueSummary(): Promise<RevenueSummary> {
  const { data } = await client.get('/api/v1/admin/billing/revenue', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<RevenueSummary>
  return resp.data || {}
}

export async function getFailedPayments(): Promise<Subscription[]> {
  const { data } = await client.get('/api/v1/admin/billing/failures', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Subscription[]>
  return resp.data || []
}

// Chat Service Types
export type ChatMessage = {
  messageId?: string
  conversationId?: string
  userMessage?: string
  aiResponse?: string
  timestamp?: string
  userId?: number
  suggestedTopics?: string[]
}

export type ChatConversation = {
  conversationId?: string
  userId?: number
  title?: string
  createdAt?: string
  updatedAt?: string
  messages?: ChatMessage[]
  messageCount?: number
}

export type CreateConversationRequest = {
  userId: number
  title?: string
}

export type SendMessageRequest = {
  message: string
  userId: number
  conversationId: string
}

export type SendMessageWithSystemPromptRequest = {
  systemPrompt: string
  queryMessage: string
  userId: number
  conversationId: string
}

// Chat Service API Functions
export async function createConversation(request: CreateConversationRequest): Promise<ChatConversation> {
  const { data } = await client.post('/api/v1/chat/conversations', request, {
    headers: { accept: 'application/json', 'Content-Type': 'application/json' }
  })
  // Response is returned directly (not wrapped in ApiResponse)
  return data as ChatConversation
}

export async function sendChatMessage(request: SendMessageRequest): Promise<ChatMessage> {
  const { data } = await client.post('/api/v1/chat/message', request, {
    headers: { accept: 'application/json', 'Content-Type': 'application/json' }
  })
  // Response is returned directly (ChatMessageResponse)
  return data as ChatMessage
}

export async function sendChatMessageWithSystemPrompt(request: SendMessageWithSystemPromptRequest): Promise<ChatMessage> {
  const { data } = await client.post('/api/v1/chat/message/with-system-prompt', request, {
    headers: { accept: 'application/json', 'Content-Type': 'application/json' }
  })
  // Response is returned directly (ChatMessageResponse)
  return data as ChatMessage
}

export async function getUserConversations(userId: number): Promise<ChatConversation[]> {
  const { data } = await client.get('/api/v1/chat/conversations', {
    params: { userId },
    headers: { accept: 'application/json' }
  })
  // Response is Page<ChatConversationResponse> with content array
  if (data && typeof data === 'object' && 'content' in data) {
    const page = data as Page<ChatConversation>
    return Array.isArray(page.content) ? page.content : []
  }
  // Fallback: handle ApiResponse wrapper or direct array
  if (data && typeof data === 'object' && 'data' in data) {
    const resp = data as ApiResponse<ChatConversation[]>
    return Array.isArray(resp.data) ? resp.data : []
  }
  return Array.isArray(data) ? (data as ChatConversation[]) : []
}

export async function getConversationMessages(conversationId: string): Promise<ChatConversation> {
  const { data } = await client.get(`/api/v1/chat/conversations/${encodeURIComponent(conversationId)}/messages`, {
    headers: { accept: 'application/json' }
  })
  // Response is Page<ChatMessageResponse> with messages in content array
  if (data && typeof data === 'object' && 'content' in data) {
    const page = data as Page<ChatMessage>
    // Return ChatConversation with messages from content array
    return {
      conversationId,
      messages: Array.isArray(page.content) ? page.content : [],
      messageCount: page.content?.length || 0
    } as ChatConversation
  }
  // Fallback: if response is already ChatConversation format
  const conv = data as ChatConversation
  return conv || { conversationId, messages: [], messageCount: 0 }
}

export async function deleteConversation(conversationId: string): Promise<void> {
  await client.delete(`/api/v1/chat/conversations/${encodeURIComponent(conversationId)}`, {
    headers: { accept: 'application/json' }
  })
  // Returns 204 No Content
}

// Media Management Types
export type MediaMetadata = {
  [key: string]: any
  duration?: number
  thumbnail?: string
  url?: string
  resolution?: string
  format?: string
  width?: number
  height?: number
  bitrate?: number
  pages?: number
  fileSize?: number
}

export type MediaItem = {
  id?: string
  title?: string
  description?: string
  category?: string
  subcategory?: string
  mediaType?: 'VIDEO' | 'IMAGE' | 'AUDIO' | 'DOCUMENT'
  metadata?: MediaMetadata
  createdAt?: string
  updatedAt?: string
}

export type CreateMediaRequest = {
  title: string
  description?: string
  category: string
  subcategory?: string
  mediaType: 'VIDEO' | 'IMAGE' | 'AUDIO' | 'DOCUMENT'
  metadata?: MediaMetadata
}

// Media Management API Functions
export async function getMedia(category?: string, mediaType?: string): Promise<MediaItem[]> {
  const params: Record<string, any> = {}
  if (category) params.category = category
  if (mediaType) params.mediaType = mediaType
  const { data } = await client.get('/api/client/media', {
    params,
    headers: { accept: 'application/json' }
  })
  const resp = data as ApiResponse<MediaItem[]>
  return resp.data || []
}

export async function getMediaById(mediaId: string): Promise<MediaItem> {
  const { data } = await client.get(`/api/client/media/${encodeURIComponent(mediaId)}`, {
    headers: { accept: 'application/json' }
  })
  const resp = data as ApiResponse<MediaItem>
  return resp.data as MediaItem
}

export async function createMedia(request: CreateMediaRequest): Promise<ApiResponse<MediaItem>> {
  const { data } = await client.post('/api/client/media', request, {
    headers: { accept: 'application/json', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<MediaItem>
}

export async function deleteMedia(mediaId: string): Promise<ApiResponse<void>> {
  const { data } = await client.delete(`/api/client/media/${encodeURIComponent(mediaId)}`, {
    headers: { accept: 'application/json' }
  })
  return data as ApiResponse<void>
}

// Topic Management Types
export type SuggestiveQuestion = {
  question: string
  subtitle?: string
}

export type Topic = {
  id?: string
  title?: string
  suggestiveQuestions?: SuggestiveQuestion[]
  createdAt?: string
  updatedAt?: string
}

export type CreateTopicRequest = {
  title: string
  suggestiveQuestions: SuggestiveQuestion[]
}

export type UpdateTopicRequest = {
  title: string
  suggestiveQuestions: SuggestiveQuestion[]
}

// Topic Management API Functions
export async function getTopics(): Promise<Topic[]> {
  const { data } = await client.get('/api/client/topics', {
    headers: { accept: 'application/json' }
  })
  const resp = data as ApiResponse<Topic[]>
  return resp.data || []
}

export async function getTopicById(topicId: string): Promise<Topic> {
  const { data } = await client.get(`/api/client/topics/${encodeURIComponent(topicId)}`, {
    headers: { accept: 'application/json' }
  })
  const resp = data as ApiResponse<Topic>
  return resp.data as Topic
}

export async function createTopic(request: CreateTopicRequest): Promise<ApiResponse<Topic>> {
  const { data } = await client.post('/api/client/topics', request, {
    headers: { accept: 'application/json', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<Topic>
}

export async function updateTopic(topicId: string, request: UpdateTopicRequest): Promise<ApiResponse<Topic>> {
  const { data } = await client.put(`/api/client/topics/${encodeURIComponent(topicId)}`, request, {
    headers: { accept: 'application/json', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<Topic>
}

export async function deleteTopic(topicId: string): Promise<ApiResponse<void>> {
  const { data } = await client.delete(`/api/client/topics/${encodeURIComponent(topicId)}`, {
    headers: { accept: 'application/json' }
  })
  return data as ApiResponse<void>
}

// ------------------------------------------------------------------
// Admin Transaction Remediation API
// ------------------------------------------------------------------

export type RegexPatternDTO = {
  id: string
  regexPattern: string
  exampleSms: string
  usageCount: number
}

export type RemediationSampleDTO = {
  transactionId: string
  rawSms: string
  regexPattern: string
  currentBadMerchant: string
  proposedNewMerchant: string
  salvagedFromGroup: string
}

export type RemediationResponseDTO = {
  totalAffectedCount: number
  salvageableCount: number
  unsalvageableCount: number
  samples?: RemediationSampleDTO[]
  salvageableSamples?: RemediationSampleDTO[]
  unsalvageableSamples?: RemediationSampleDTO[]
}

export type RegexRemediationResponseDTO = {
  totalCount: number
  patterns: RegexPatternDTO[]
}

// Domain 1: Missing Merchant
export async function getMissingMerchantBadRegex(): Promise<ApiResponse<RegexRemediationResponseDTO>> {
  const { data } = await client.get('/api/admin/regex/remediation/missing-merchant', {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<RegexRemediationResponseDTO>
}

export async function deleteMissingMerchantBadRegex(): Promise<ApiResponse<number>> {
  const { data } = await client.delete('/api/admin/regex/remediation/missing-merchant', {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<number>
}

export async function getMissingMerchantAffectedTransactions(page = 0, size = 20): Promise<Page<AdminTransaction>> {
  const { data } = await client.get('/api/admin/transactions/remediation/missing-merchant/affected', {
    params: { page, size },
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<AdminTransaction>>
  return resp.data as Page<AdminTransaction>
}

export async function dryRunMissingMerchantFix(): Promise<ApiResponse<RemediationResponseDTO>> {
  const { data } = await client.post('/api/admin/transactions/remediation/missing-merchant/dry-run', {}, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<RemediationResponseDTO>
}

export async function executeMissingMerchantFix(): Promise<ApiResponse<RemediationResponseDTO>> {
  const { data } = await client.post('/api/admin/transactions/remediation/missing-merchant/execute', {}, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<RemediationResponseDTO>
}

// Domain 2: Verbal Merchant
export async function searchVerbalMerchantBadRegex(badMerchants: string[]): Promise<ApiResponse<RegexRemediationResponseDTO>> {
  const { data } = await client.post('/api/admin/regex/remediation/verbal-merchant/search', { badMerchants }, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<RegexRemediationResponseDTO>
}

export async function deleteVerbalMerchantBadRegex(badMerchants: string[]): Promise<ApiResponse<number>> {
  const { data } = await client.post('/api/admin/regex/remediation/verbal-merchant/delete', { badMerchants }, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<number>
}

export async function getVerbalMerchantAffectedTransactions(badMerchants: string[], page = 0, size = 20): Promise<Page<AdminTransaction>> {
  const { data } = await client.post('/api/admin/transactions/remediation/verbal-merchant/affected', { badMerchants }, {
    params: { page, size },
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  const resp = data as ApiResponse<Page<AdminTransaction>>
  return resp.data as Page<AdminTransaction>
}

export async function dryRunVerbalMerchantFix(badMerchants: string[]): Promise<ApiResponse<RemediationResponseDTO>> {
  const { data } = await client.post('/api/admin/transactions/remediation/verbal-merchant/dry-run', { badMerchants }, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<RemediationResponseDTO>
}

export async function executeVerbalMerchantFix(badMerchants: string[]): Promise<ApiResponse<RemediationResponseDTO>> {
  const { data } = await client.post('/api/admin/transactions/remediation/verbal-merchant/execute', { badMerchants }, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<RemediationResponseDTO>
}

export async function getUnknownMerchantTransactions(page = 0, size = 20): Promise<Page<AdminTransaction>> {
  const { data } = await client.get('/api/admin/transactions/unknown-merchant', {
    params: { page, size },
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<AdminTransaction>>
  return resp.data as Page<AdminTransaction>
}

// ---- AI Usage (token accounting) ----

/**
 * Feature keys written by the backend runners (`AI_` + enum name).
 *
 * NOTE: `AI_INSIGHTS_LIVE` / `AI_INSIGHTS_HISTORICAL` (plural) were NEVER
 * written — the real key is `AI_INSIGHT_LIVE` (singular). Both plural
 * constants are @Deprecated server-side. Match on the `AI_INSIGHT` prefix
 * rather than an exact string.
 *
 * Only AI_CHAT, AI_SMS_PARSE, AI_MERCHANT_CAT, AI_INSIGHT_LIVE and
 * AI_TOPIC_QUESTIONS can be written today; the rest are history and flatline.
 */
export type AIFeatureKey =
  // currently written
  | 'AI_CHAT'
  | 'AI_SMS_PARSE'
  | 'AI_MERCHANT_CAT'
  | 'AI_INSIGHT_LIVE'
  | 'AI_TOPIC_QUESTIONS'
  // historical only (retired insight types)
  | 'AI_INSIGHT_HISTORICAL'
  | 'AI_TIP_SAVINGS_RATE'
  | 'AI_TIP_DAILY_SPEND'
  | 'AI_TIP_DAILY_SAVING'
  | 'AI_QUICK_WIN'
  | 'AI_WATCH_OUT'
  | 'AI_WIN'

export type FeatureTokenStats = {
  callCount: number
  promptTokens: number
  completionTokens: number
  totalTokens: number
  /**
   * Subset of promptTokens (NOT additive) — the slice served from the
   * provider's prefix cache, billed at the cached input rate.
   * Cache hit rate = cachedPromptTokens / promptTokens.
   * Zero on all historical rows (nothing backfills it).
   */
  cachedPromptTokens?: number
  /** Sum of per-call latency in ms. Mean = latencyMsSum / callCount. */
  latencyMsSum?: number
  /** Slowest single call in ms. */
  latencyMsMax?: number
  model?: string | null
  costUsd?: string | null
}

export type CostBreakdown = {
  /**
   * These are now a real PARTIAL SUM — they no longer come back null just
   * because one bucket in the window had an unpriced model. Check
   * unpricedTokens to know whether the total is complete.
   */
  promptUsd: string | null
  completionUsd: string | null
  totalUsd: string | null
  /** Tokens that could not be priced. 0 → the total above is complete. */
  unpricedTokens?: number
}

export type UsageCounter = {
  id?: string
  userId: number
  date: string
  aiChatCount?: number
  aiInsightsCount?: number
  tokensUsed: number
  promptTokens: number
  completionTokens: number
  // Keys may be plain `AIFeatureKey` (legacy rows) or `${AIFeatureKey}::${model}` (post Phase-4).
  // Use parseFeatureTokenKey() to split.
  featureTokens?: Record<string, FeatureTokenStats>
  costs?: CostBreakdown
  createdAt?: string
  updatedAt?: string
}

export type UserUsageStats = {
  id?: string
  userId: number
  mobileNumber?: string | null
  currentPeriod: string
  monthlyChatCount?: number
  monthlyTokensUsed: number
  monthlyPromptTokens: number
  monthlyCompletionTokens: number
  // Keys may be plain `AIFeatureKey` (legacy rows) or `${AIFeatureKey}::${model}` (post Phase-4).
  // Use parseFeatureTokenKey() to split.
  featureTokens?: Record<string, FeatureTokenStats>
  costs?: CostBreakdown
  createdAt?: string
  updatedAt?: string
}

export type AIFeatureSummary = {
  callCount: number
  promptTokens: number
  completionTokens: number
  totalTokens: number
  costUsd: string | null
  // Present when the backend rolls the new per-call metrics into summaries.
  cachedPromptTokens?: number
  latencyMsSum?: number
  latencyMsMax?: number
}

export type AIProviderKey = 'kimi' | 'anthropic' | 'unknown'

export type AIUsageSummary = {
  windowFrom: string
  windowTo: string
  totalCallCount: number
  totalPromptTokens: number
  totalCompletionTokens: number
  totalTokens: number
  totalCostUsd: string | null
  byFeature: Partial<Record<AIFeatureKey, AIFeatureSummary>>
  byProvider?: Partial<Record<AIProviderKey, AIFeatureSummary>>
  uniqueUsers: number
}

/**
 * Parse a featureTokens map key into its feature + model parts.
 * Post Phase 4, keys can be `AI_INSIGHTS_LIVE::kimi-k2.6`. Legacy rows
 * still use plain `AI_INSIGHTS_LIVE`. Returns null model for legacy.
 */
export function parseFeatureTokenKey(key: string): { feature: string; model: string | null } {
  const idx = key.indexOf('::')
  if (idx < 0) return { feature: key, model: null }
  return { feature: key.slice(0, idx), model: key.slice(idx + 2) || null }
}

/**
 * Provider for a given model name. Mirrors the server-side prefix rule.
 */
export function providerForModel(model: string | null | undefined): AIProviderKey {
  if (!model) return 'unknown'
  if (model.startsWith('kimi-')) return 'kimi'
  if (model.startsWith('claude-')) return 'anthropic'
  return 'unknown'
}

export type TopSpender = {
  userId: number
  mobileNumber?: string | null
  monthlyTokensUsed: number
  callCount: number
  monthlyCostUsd: string | null
}

export type CostTrendPoint = {
  date: string
  totalCostUsd: string | null
  byFeature: Partial<Record<AIFeatureKey, string>>
  byProvider?: Partial<Record<AIProviderKey, string>>
}

export type AICostTrend = {
  windowFrom: string
  windowTo: string
  groupBy: string
  series: CostTrendPoint[]
}

export type AISpendProjection = {
  period: string
  daysElapsed: number
  daysInMonth: number
  spentSoFarUsd: string | null
  projectedTotalUsd: string | null
  projectionMethod: string
}

export type ModelRate = {
  inputUsdPerMillion: string
  outputUsdPerMillion: string
  /** Rate for prefix-cached input tokens. Optional — absent on older models. */
  cachedInputUsdPerMillion?: string
}

export type AICostRates = {
  currentModel: string
  legacyDefault: string
  rates: Record<string, ModelRate>
  effectiveFrom: string
}

export type AIBalance = {
  availableUsd: string | null
  voucherUsd: string | null
  cashUsd: string | null
  fetchedAt: string
  stale: boolean
}

export type AIRunwayIndicator = 'green' | 'amber' | 'red'

export type AIRunway = {
  availableUsd: string | null
  avgDailyBurnUsd: string | null
  daysOfRunway: number | null
  burnWindowDays: number
  indicator: AIRunwayIndicator
  fetchedAt: string
}

export type FeaturePerUserStats = {
  mean: string
  median: string
  p75: string
  p90: string
  p99: string
  max: string
}

export type FeatureExtrapolationStats = {
  activeUsers: number
  activationRate: string
  historicalCostUsd: string | null
  perUserStats: FeaturePerUserStats
}

export type ExtrapolationMethod = 'LINEAR_TOTAL' | 'LINEAR_BY_FEATURE' | 'PERCENTILE_MIX'

export type CostExtrapolationEstimate = {
  method: ExtrapolationMethod
  description: string
  projectedCostUsd: string | null
  byFeature: Partial<Record<AIFeatureKey, string>>
  notes: string
}

export type AICostExtrapolation = {
  targetUsers: number
  targetDays: number
  basis: {
    sampleFrom: string
    sampleTo: string
    sampleDays: number
    historicalActiveUsers: number
    historicalTotalCostUsd: string | null
  }
  perFeature: Partial<Record<AIFeatureKey, FeatureExtrapolationStats>>
  estimates: CostExtrapolationEstimate[]
  warnings: string[]
}

export async function getDailyAIUsage(params: {
  page?: number
  size?: number
  userId?: number
  startDate?: string
  endDate?: string
  minTokensUsed?: number
}): Promise<Page<UsageCounter>> {
  const { data } = await client.get('/api/admin/ai-usage/daily', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<UsageCounter>>
  return resp.data as Page<UsageCounter>
}

export async function getMonthlyAIUsage(params: {
  page?: number
  size?: number
  userId?: number
  mobileNumber?: string
  period?: string
  minTokensUsed?: number
}): Promise<Page<UserUsageStats>> {
  const { data } = await client.get('/api/admin/ai-usage/monthly', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Page<UserUsageStats>>
  return resp.data as Page<UserUsageStats>
}

export async function getAIUsageSummary(params: {
  from?: string
  to?: string
  userId?: number
}): Promise<AIUsageSummary> {
  const { data } = await client.get('/api/admin/ai-usage/summary', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AIUsageSummary>
  return resp.data as AIUsageSummary
}

export async function getAITopSpenders(params: {
  period?: string
  limit?: number
  provider?: AIProviderKey
}): Promise<TopSpender[]> {
  const { data } = await client.get('/api/admin/ai-usage/top-spenders', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<TopSpender[]>
  return (resp.data as TopSpender[]) || []
}

export async function getAICostTrend(params: {
  from?: string
  to?: string
}): Promise<AICostTrend> {
  const { data } = await client.get('/api/admin/ai-usage/cost-trend', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AICostTrend>
  return resp.data as AICostTrend
}

export async function getAISpendProjection(params: {
  period?: string
}): Promise<AISpendProjection> {
  const { data } = await client.get('/api/admin/ai-usage/projection', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AISpendProjection>
  return resp.data as AISpendProjection
}

export async function getAICostRates(): Promise<AICostRates> {
  const { data } = await client.get('/api/admin/ai-usage/cost-rates', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AICostRates>
  return resp.data as AICostRates
}

export async function getAIBalance(): Promise<AIBalance> {
  const { data } = await client.get('/api/admin/ai-usage/balance', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AIBalance>
  return resp.data as AIBalance
}

export async function getAIRunway(params: {
  burnWindowDays?: number
} = {}): Promise<AIRunway> {
  const { data } = await client.get('/api/admin/ai-usage/runway', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AIRunway>
  return resp.data as AIRunway
}

export async function getAICostExtrapolation(params: {
  targetUsers?: number
  targetDays?: number
  sampleDays?: number
}): Promise<AICostExtrapolation> {
  const { data } = await client.get('/api/admin/ai-usage/extrapolate', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AICostExtrapolation>
  return resp.data as AICostExtrapolation
}

// ---- Phase 1 §1.4: by-provider focused rollup ----

export async function getAIByProvider(params: {
  from?: string
  to?: string
  userId?: number
}): Promise<Partial<Record<AIProviderKey, AIFeatureSummary>>> {
  const { data } = await client.get('/api/admin/ai-usage/by-provider', {
    params,
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<Partial<Record<AIProviderKey, AIFeatureSummary>>>
  return resp.data || {}
}

// ---- Phase 2: Anthropic spend proxy ----

export type AnthropicSpend = {
  windowFrom: string
  windowTo: string
  costUsd: string | number | null
  inputTokens: number
  outputTokens: number
  cacheReadTokens: number
  cacheWriteTokens: number
  fetchedAt: string
  stale: boolean
}

/**
 * Returns Anthropic spend for the window, or null when the server returns
 * 503 because ANTHROPIC_ADMIN_KEY isn't configured. Caller renders an
 * "Admin key not configured" empty state in that case.
 */
export async function getAnthropicSpend(params: {
  from?: string
  to?: string
}): Promise<AnthropicSpend | null> {
  try {
    const { data } = await client.get('/api/admin/ai-usage/anthropic/spend', {
      params,
      headers: { accept: '*/*' }
    })
    const resp = data as ApiResponse<AnthropicSpend>
    return resp.data || null
  } catch (e: any) {
    if (e?.response?.status === 503) return null
    throw e
  }
}

export async function getAnthropicMtd(): Promise<AnthropicSpend | null> {
  try {
    const { data } = await client.get('/api/admin/ai-usage/anthropic/mtd', {
      headers: { accept: '*/*' }
    })
    const resp = data as ApiResponse<AnthropicSpend>
    return resp.data || null
  } catch (e: any) {
    if (e?.response?.status === 503) return null
    throw e
  }
}

// ---- Phase 3: Insights config (runtime model overrides) ----

export type InsightType =
  | 'insight_live'
  | 'insight_historical'
  | 'tip_savings_rate'
  | 'tip_daily_spend'
  | 'tip_daily_saving'
  | 'quick_win'
  | 'watch_out'
  | 'win'

export type InsightModelSource = 'yaml' | 'override'

export type InsightModelConfig = {
  type?: InsightType
  yamlModel: string | null
  overrideModel: string | null
  effectiveModel: string | null
  source: InsightModelSource
}

export type InsightsConfig = {
  defaultProvider: string
  stalenessCheckModel: string
  providers: Partial<Record<InsightType, InsightModelConfig>>
  knownTypes?: Partial<Record<InsightType, { enumName: string }>>
  knownModels: string[]
}

export async function getInsightsConfig(): Promise<InsightsConfig> {
  const { data } = await client.get('/api/admin/insights/config', {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<InsightsConfig>
  return resp.data as InsightsConfig
}

export async function getInsightModel(type: InsightType): Promise<InsightModelConfig> {
  const { data } = await client.get(`/api/admin/insights/config/${type}/model`, {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<InsightModelConfig>
  return resp.data as InsightModelConfig
}

export async function setInsightModel(
  type: InsightType,
  body: { model: string; description?: string }
): Promise<ApiResponse<InsightModelConfig>> {
  const { data } = await client.put(
    `/api/admin/insights/config/${type}/model`,
    body,
    { headers: { accept: '*/*', 'Content-Type': 'application/json' } }
  )
  return data as ApiResponse<InsightModelConfig>
}

export async function clearInsightModel(type: InsightType): Promise<ApiResponse<InsightModelConfig>> {
  const { data } = await client.delete(`/api/admin/insights/config/${type}/model`, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<InsightModelConfig>
}

// ---- Phase 4: Ask AI Chat Model Switcher & Client Salary/Savings ----

export type ChatModelConfig = {
  yamlDefault: string | null
  override: string | null
  effective: string | null
  source: 'yaml' | 'override'
  knownModels: string[]
}

export type SalaryPeriodDTO = {
  id?: string | number
  userId?: number
  salaryAmount: number
  effectiveFrom: string
  effectiveTo?: string | null
  streamNames?: string[]
  primaryStreamIndex?: number
  createdAt?: string
}

export type SalaryPeriodRequest = {
  salaryAmount: number
  effectiveFrom: string
  effectiveTo?: string | null
  streamNames?: string[]
  primaryStreamIndex?: number
}

export type PayCycleDTO = {
  cycleStartDate?: string
  cycleEndDate?: string
  expectedIncome?: number
  actualIncome?: number
  spendInCycle?: number
  savings?: number
  savingsRate?: number
  inProgress?: boolean
}

// Chat Model Config Switcher
export async function getChatModelConfig(): Promise<ChatModelConfig> {
  const { data } = await client.get('/api/admin/chat/config/model', {
    headers: { accept: '*/*' }
  })
  const resp = data as (ApiResponse<ChatModelConfig> | ChatModelConfig)
  return 'data' in resp && resp.data ? resp.data : (resp as ChatModelConfig)
}

export async function setChatModelOverride(body: { model: string; description?: string }): Promise<ApiResponse<ChatModelConfig>> {
  const { data } = await client.put('/api/admin/chat/config/model', body, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<ChatModelConfig>
}

export async function clearChatModelOverride(): Promise<ApiResponse<ChatModelConfig>> {
  const { data } = await client.delete('/api/admin/chat/config/model', {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<ChatModelConfig>
}

// Salary Period Timeline
export async function getSalaryTimeline(userId: number): Promise<SalaryPeriodDTO[]> {
  const { data } = await client.get(`/api/client/users/${userId}/salary/timeline`, {
    headers: { accept: '*/*' }
  })
  const resp = data as (ApiResponse<SalaryPeriodDTO[]> | SalaryPeriodDTO[])
  return 'data' in resp && resp.data ? resp.data : (resp as unknown as SalaryPeriodDTO[])
}

export async function addSalaryPeriod(userId: number, body: SalaryPeriodRequest): Promise<ApiResponse<SalaryPeriodDTO>> {
  const { data } = await client.post(`/api/client/users/${userId}/salary/periods`, body, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<SalaryPeriodDTO>
}

export async function updateSalaryPeriod(userId: number, periodId: string | number, body: SalaryPeriodRequest): Promise<ApiResponse<SalaryPeriodDTO>> {
  const { data } = await client.put(`/api/client/users/${userId}/salary/periods/${periodId}`, body, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  return data as ApiResponse<SalaryPeriodDTO>
}

export async function deleteSalaryPeriod(userId: number, periodId: string | number): Promise<ApiResponse<void>> {
  const { data } = await client.delete(`/api/client/users/${userId}/salary/periods/${periodId}`, {
    headers: { accept: '*/*' }
  })
  return data as ApiResponse<void>
}

// Cycle Savings
export async function getCycleSavings(userId: number, month?: string): Promise<PayCycleDTO> {
  const { data } = await client.get(`/api/client/users/${userId}/savings`, {
    params: month ? { month } : undefined,
    headers: { accept: '*/*' }
  })
  const resp = data as (ApiResponse<PayCycleDTO> | PayCycleDTO)
  return 'data' in resp && resp.data ? resp.data : (resp as unknown as PayCycleDTO)
}

// ---- Funnel Observability & Trigger Stats ----

export type TriggerStatsRow = {
  day: string
  dataKind: string
  events: number
  stage1: number
  stage2: number
  stage3: number
  regenOk: number
  regenFail: number
}

export type TriggerStatsResponse = {
  rows: TriggerStatsRow[]
  totals: {
    events: number
    stage1: number
    stage2: number
    stage3: number
    regenOk: number
    regenFail: number
  }
}

export async function getTriggerStats(params: {
  from: string
  to: string
  userId?: string | number | null
}): Promise<TriggerStatsResponse> {
  const { data } = await client.get('/api/admin/insights/triggers/stats', {
    params: {
      from: params.from,
      to: params.to,
      userId: params.userId || undefined
    },
    headers: { accept: '*/*' }
  })
  const resp = data as (ApiResponse<TriggerStatsResponse> | TriggerStatsResponse)
  return 'data' in resp && resp.data ? resp.data : (resp as unknown as TriggerStatsResponse)
}

// ---- App Version / force-update config (per platform) ----

export type AppVersionPlatform = 'android' | 'ios'

export type AppVersionConfig = {
  platform?: string
  hardVersion?: string | null   // below this → HARD/forced (blocking)
  softVersion?: string | null   // below this (>= hard) → SOFT/optional
  storeUrl?: string | null
  forcedTitle?: string | null
  forcedMessage?: string | null
  optionalTitle?: string | null
  optionalMessage?: string | null
}

// Preview verdict a client on a given version would receive. Shape is
// rendered flexibly on the frontend; known fields are surfaced, the rest
// shown generically.
export type AppVersionPreview = {
  updateType?: string | null   // e.g. FORCED / OPTIONAL / NONE
  forced?: boolean | null
  blocking?: boolean | null
  title?: string | null
  message?: string | null
  storeUrl?: string | null
  version?: string | null
  [k: string]: any
}

export async function getAppVersionConfig(platform: AppVersionPlatform): Promise<AppVersionConfig> {
  const { data } = await client.get(`/api/admin/app-version/${platform}`, {
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AppVersionConfig>
  return (resp.data as AppVersionConfig) || {}
}

export async function updateAppVersionConfig(
  platform: AppVersionPlatform,
  body: Partial<AppVersionConfig>
): Promise<AppVersionConfig> {
  const { data } = await client.put(`/api/admin/app-version/${platform}`, body, {
    headers: { accept: '*/*', 'Content-Type': 'application/json' }
  })
  const resp = data as ApiResponse<AppVersionConfig>
  return (resp.data as AppVersionConfig) || {}
}

export async function getAppVersionPreview(
  platform: AppVersionPlatform,
  version: string
): Promise<AppVersionPreview> {
  const { data } = await client.get(`/api/admin/app-version/${platform}/preview`, {
    params: { version },
    headers: { accept: '*/*' }
  })
  const resp = data as ApiResponse<AppVersionPreview>
  return (resp.data as AppVersionPreview) || {}
}

